import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDaysLeft } from "@/lib/date";
import { AdminTeamDTO } from "@shaarp/common";

export default function DataTableCellAdminsNextBilling({
  team,
}: {
  team: AdminTeamDTO;
}) {
  return (
    <div className="flex gap-1">
      <MonthlySubscriptionBadge team={team} />
      <AnnualSubscriptionBadge team={team} />
    </div>
  );
}

const MonthlySubscriptionBadge = ({ team }: { team: AdminTeamDTO }) => {
  const { t, currentLanguage } = useTranslation();

  // Vérifier s'il y a une résiliation programmée
  const hasMonthlyEnd = team.stripeMonthlySubscriptionEndsAt;
  const hasMonthlyPeriod = team.stripeMonthlySubscriptionCurrentPeriodEndsAt;

  if (!hasMonthlyPeriod && !hasMonthlyEnd) {
    return null;
  }

  // Prioriser la résiliation si elle existe
  if (hasMonthlyEnd) {
    const endDate = new Date(team.stripeMonthlySubscriptionEndsAt!);
    const daysLeft = convertDaysLeft(endDate);
    const isEnded = daysLeft <= 0;

    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            variant={isEnded ? "outline" : "warning"}
            className="whitespace-nowrap"
          >
            {isEnded
              ? `M: ${t("admin.dataTableAdminTeams.badges.canceled")}`
              : `M: Fin J-${daysLeft}`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {isEnded
            ? t("admin.dataTableAdminTeams.tooltips.subscriptionCanceledAt", {
                date: endDate.toLocaleDateString(currentLanguage),
              })
            : t("admin.dataTableAdminTeams.tooltips.subscriptionEndsAt", {
                date: endDate.toLocaleDateString(currentLanguage),
              })}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Sinon afficher le prochain prélèvement
  const periodEndDate = new Date(
    team.stripeMonthlySubscriptionCurrentPeriodEndsAt!
  );
  const daysLeft = convertDaysLeft(periodEndDate);

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger>
        <Badge variant="success" className="whitespace-nowrap">
          M: J-{daysLeft}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {t("admin.dataTableAdminTeams.tooltips.nextBilling", {
          date: periodEndDate.toLocaleDateString(currentLanguage),
        })}
      </TooltipContent>
    </Tooltip>
  );
};

const AnnualSubscriptionBadge = ({ team }: { team: AdminTeamDTO }) => {
  const { t, currentLanguage } = useTranslation();

  // Vérifier s'il y a une résiliation programmée
  const hasAnnualEnd = team.stripeAnnualSubscriptionEndsAt;
  const hasAnnualPeriod = team.stripeAnnualSubscriptionCurrentPeriodEndsAt;

  if (!hasAnnualPeriod && !hasAnnualEnd) {
    return null;
  }

  // Prioriser la résiliation si elle existe
  if (hasAnnualEnd) {
    const endDate = new Date(team.stripeAnnualSubscriptionEndsAt!);
    const daysLeft = convertDaysLeft(endDate);
    const isEnded = daysLeft <= 0;

    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <Badge
            variant={isEnded ? "outline" : "warning"}
            className="whitespace-nowrap"
          >
            {isEnded
              ? `A: ${t("admin.dataTableAdminTeams.badges.canceled")}`
              : `A: Fin J-${daysLeft}`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {isEnded
            ? t("admin.dataTableAdminTeams.tooltips.subscriptionCanceledAt", {
                date: endDate.toLocaleDateString(currentLanguage),
              })
            : t("admin.dataTableAdminTeams.tooltips.subscriptionEndsAt", {
                date: endDate.toLocaleDateString(currentLanguage),
              })}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Sinon afficher le prochain prélèvement
  const periodEndDate = new Date(
    team.stripeAnnualSubscriptionCurrentPeriodEndsAt!
  );
  const daysLeft = convertDaysLeft(periodEndDate);

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger>
        <Badge variant="success" className="whitespace-nowrap">
          A: J-{daysLeft}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {t("admin.dataTableAdminTeams.tooltips.nextBilling", {
          date: periodEndDate.toLocaleDateString(currentLanguage),
        })}
      </TooltipContent>
    </Tooltip>
  );
};
